import CustomTransferList from 'Components/CustomTransferList/CustomTransferList'
import { WithLoader } from 'Components/WithLoader'
import { useUpdateSingleCatalogMutation } from 'Services/ProductManagement/Features/Catalog/catalogApi'
import type { Catalog } from 'Services/ProductManagement/Features/Catalog/types'
import useProductsEdit from 'Services/ProductManagement/Features/Product/Hooks/useProductsEdit'
import useTariffCatalogProducts from 'Services/ProductManagement/Features/Product/Hooks/useTariffCatalogProducts'
import useTariffProducts from 'Services/ProductManagement/Features/Product/Hooks/useTariffProducts'
import type { Product } from 'Services/ProductManagement/Features/Product/productSlice'
import type { Tariff } from 'Services/ProductManagement/Features/Tariff/types'
import { IS_WALTTI_SERVICE_ENVIRONMENT } from 'Util/constants'
import getTranslation from 'Util/getTranslation'
import SnackbarUtils from 'Util/Snackbar'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField
} from '@mui/material'
import ListItemText from '@mui/material/ListItemText'
import { useId, useMemo } from 'react'
import { t } from 'ttag'

interface TariffCatalogProductsEditProps {
  tariff: Tariff
  catalog: Catalog
  handleCancel: () => void
  handleSaveSuccess?: () => void
}

function TariffCatalogProductsEdit({
  tariff,
  catalog,
  handleCancel,
  handleSaveSuccess
}: Readonly<TariffCatalogProductsEditProps>) {
  const productGroupElementId = useId()

  const { allTariffProducts, allTariffProductsLoading, fetchAllTariffProductsWithoutPagination } =
    useTariffProducts(tariff.id)
  const [updateSingleCatalog, { isLoading: isUpdateLoading }] = useUpdateSingleCatalogMutation()
  const {
    allTariffCatalogProducts,
    allTariffCatalogProductsLoading,
    fetchAllTariffCatalogProductsWithoutPagination
  } = useTariffCatalogProducts(tariff.id, catalog.id)

  /**
   * Filters out the products that are already in the selected catalog.
   *
   * @returns Product[] - Array of products that are not already in the selected catalog.
   */
  const tariffProductsWithoutCatalogProducts = useMemo(() => {
    return allTariffProducts.filter(
      product =>
        !allTariffCatalogProducts.some(
          tariffCatalogProduct => tariffCatalogProduct.product_code === product.product_code
        )
    )
  }, [allTariffProducts, allTariffCatalogProducts])

  const {
    searchText,
    productGroupFilter,
    productGroupOptions,
    isProductGroupParameterGroupLoading,
    handleSearchTextChange,
    handleProductGroupFilterChange,
    filterOptions
  } = useProductsEdit()

  /**
   * Handles saving the selected catalog's products.
   *
   * @param {Product[]} selectedItems - The list of products that are currently selected for the catalog.
   */
  const handleSave = (selectedItems: Product[]) => {
    let catalogUpdateData = {}
    if (IS_WALTTI_SERVICE_ENVIRONMENT) {
      // For Waltti, we only update the catalog with the newly added products,
      // so we filter the items that are not yet in the selected catalog's product IDs
      const newItems = selectedItems.filter(item => !catalog.product_ids.includes(item.id))
      // Find products that were removed (in original catalog but not in selected items)
      const selectedItemIds = new Set(selectedItems.map(item => item.id))
      const removedProductIds = catalog.product_ids.filter(
        productId => !selectedItemIds.has(productId)
      )
      catalogUpdateData = {
        added_products: newItems, // Waltti needs the full product objects to create copies of the products.
        removed_product_ids: removedProductIds // Product copies that were removed from the catalog
      }
    } else {
      // For HSL, we update the catalog with all the selected products.
      catalogUpdateData = { product_ids: selectedItems.map(product => product.id) } // HSL only needs the product IDs since they don't create copies.
    }

    updateSingleCatalog({
      id: catalog.id,
      ...catalogUpdateData
    })
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Successfully saved changes.`)
        fetchAllTariffCatalogProductsWithoutPagination()
        fetchAllTariffProductsWithoutPagination()
        handleSaveSuccess?.()
      })
      .catch(e => {
        console.error(e)
        SnackbarUtils.error(t`An error occurred while saving changes.`)
      })
  }

  const renderItemContent = (product: Product) => {
    const labelId = `transfer-list-item-${product.id}-label`
    return (
      <ListItemText
        id={labelId}
        primary={`${getTranslation(product?.translations, 'fi').name}`}
        secondary={product?.product_code}
      />
    )
  }

  const isLoading =
    allTariffCatalogProductsLoading === 'pending' ||
    allTariffProductsLoading === 'pending' ||
    isProductGroupParameterGroupLoading ||
    isUpdateLoading

  return (
    <WithLoader loading={isLoading}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <FormControl sx={{ width: '48%' }}>
          <TextField
            size="small"
            sx={{ backgroundColor: 'background.default' }}
            label={t`Product search`}
            placeholder={t`Search products`}
            value={searchText}
            onChange={handleSearchTextChange}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                )
              }
            }}
          />
        </FormControl>
        <FormControl sx={{ width: '48%' }}>
          <InputLabel shrink id={productGroupElementId}>{t`Product group`}</InputLabel>
          <Select
            name="product_group"
            labelId={productGroupElementId}
            value={productGroupFilter}
            size="small"
            displayEmpty
            label={t`Product group`}
            onChange={handleProductGroupFilterChange}
            sx={{ backgroundColor: 'background.default' }}
            input={<OutlinedInput notched label={t`Product group`} />}
          >
            {productGroupOptions?.map(({ label, value }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <CustomTransferList
        leftItemsHeaderText={t`Tariff product listing`}
        rightItemsHeaderText={t`Sales channel products`}
        leftItems={tariffProductsWithoutCatalogProducts}
        rightItems={allTariffCatalogProducts}
        renderItemContent={renderItemContent}
        handleSave={handleSave}
        handleCancel={handleCancel}
        compareFn={(item: Product) => item.id}
        filterFn={filterOptions}
      />
    </WithLoader>
  )
}

export default TariffCatalogProductsEdit
